import React, { useState, useRef, useEffect } from 'react';
import { useCart } from './CartContext';
import styles from '../Cart/Cart.module.css';
import { Link, useLocation } from 'react-router-dom';

const Cart: React.FC = () => {
    const { cart, removeFromCart, updateQuantity, clearCart, getTotalPrice } = useCart();
    const location = useLocation();
    const [isExpanded, setIsExpanded] = useState(false);
    
    // State for drag functionality
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const cartRef = useRef<HTMLDivElement>(null);
    const dragStartPos = useRef({ x: 0, y: 0 });
    const cartStartPos = useRef({ x: 0, y: 0 });

    // Check if we're on a small screen
    const isMobileView = window.innerWidth <= 576;

    useEffect(() => {
        // Save the cart position in localStorage
        const savedPosition = localStorage.getItem('cartPosition');
        if (savedPosition && !isMobileView) {
            const pos = JSON.parse(savedPosition);
            setPosition(pos);
        }
    }, [isMobileView]);

    // Don't render the cart on specific pages or if it's empty
    if (location.pathname === '/checkout' || cart.length === 0) {
        return null;
    }

    const toggleCart = () => {
        setIsExpanded(!isExpanded);
    };

    // Drag functionality
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isMobileView) return;
        
        setIsDragging(true);
        dragStartPos.current = { x: e.clientX, y: e.clientY };
        cartStartPos.current = { ...position };
        
        // Prevent text selection during drag
        e.preventDefault();
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging || isMobileView) return;
        
        const dx = e.clientX - dragStartPos.current.x;
        const dy = e.clientY - dragStartPos.current.y;
        
        const newPos = {
            x: cartStartPos.current.x + dx,
            y: cartStartPos.current.y + dy
        };
        
        setPosition(newPos);
    };

    const handleMouseUp = () => {
        if (isDragging) {
            setIsDragging(false);
            // Save position to localStorage
            localStorage.setItem('cartPosition', JSON.stringify(position));
        }
    };

    // Dynamic styles for positioning the cart
    const cartStyle = !isMobileView ? {
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: isDragging ? 'grabbing' : 'auto'
    } : {};

    return (
        <div 
            className={`${styles.floatingCart} ${isExpanded && isMobileView ? styles.expanded : ''}`}
            style={cartStyle}
            ref={cartRef}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            <div 
                className={styles.cartHeader}
                onMouseDown={handleMouseDown}
            >
                {!isMobileView && <span className={styles.dragHandle}></span>}
                <h2 onClick={isMobileView ? toggleCart : undefined}>
                    Handlekurv {isMobileView && `(${cart.length})`}
                </h2>
            </div>

            <ul className="list-group">
                {cart.map((item, index) => (
                    <li key={`${item.product.id}-${item.selectedSize || index}`} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                            <strong>{item.product.name}</strong>
                            {item.selectedSize && item.product.category === "armband" && (
                                <span className="ms-2 badge bg-secondary">Str: {item.selectedSize}</span>
                            )}
                            <div>Antall: {item.quantity}</div>
                            <div className="mt-2">
                                <button className="btn btn-sm btn-secondary me-1" 
                                       onClick={() => updateQuantity(item.product.id, item.quantity - 1, item.selectedSize)}>-</button>
                                <button className="btn btn-sm btn-secondary ms-1" 
                                       onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.selectedSize)}>+</button>
                            </div>
                        </div>
                        <span>
                            {item.product.price * item.quantity} NOK
                            <button className="btn btn-sm btn-danger ms-2" 
                                   onClick={() => removeFromCart(item.product.id, item.selectedSize)}>Remove</button>
                        </span>
                    </li>
                ))}
            </ul>
            <div className="mt-3">
                <strong>Totalt: {getTotalPrice()} NOK</strong>
            </div>
            <button className="btn btn-danger mt-3 me-2" onClick={clearCart}>Slett kurv</button>
            <Link to="/checkout" className="btn btn-secondary mt-3">Utsjekk</Link>
        </div>
    );
};

export default Cart;